/**
 * Team presence over the socket (ADR 0029): who else is on the shared board, and where.
 *
 * Subscribing to the attempt topic brings a snapshot of the others; their moves then
 * arrive as frames. This tab reports its own position at most every 300 ms, says so again
 * every 20 s so the server-side entry does not expire, and reports `null` when hidden.
 * The position itself is opaque here — the puzzle type decides what it means. There is no
 * HTTP fallback: with the socket down, there are simply no cursors.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useRealtimeSocket, useTopic } from "@/core/realtime/RealtimeProvider";
import { attemptTopic, type PresencePeer } from "@/core/realtime/protocol";

/** Heartbeat while nothing moves: refreshes the entry well inside the server's TTL. */
export const PRESENCE_TICK_MS = 20_000;
/** A move is sent at most this often: a drag across the grid is a few frames, not fifty. */
export const PRESENCE_MOVE_THROTTLE_MS = 300;

export interface Presence<S = unknown> {
  peers: PresencePeer<S>[];
  /** The type's Player calls this whenever its position changes. */
  report: (state: S) => void;
}

function byUserId<S>(peers: PresencePeer<S>[]): PresencePeer<S>[] {
  // A stable order keeps each teammate's colour index stable.
  return [...peers].sort((a, b) => a.user_id - b.user_id);
}

export function usePresence<S = unknown>(attemptId: number, enabled: boolean): Presence<S> {
  const socket = useRealtimeSocket();
  const [peers, setPeers] = useState<PresencePeer<S>[]>([]);
  const stateRef = useRef<S | null>(null);
  const lastSentAt = useRef(0);
  const moveTimer = useRef<number | null>(null);
  const topic = enabled ? attemptTopic(attemptId) : null;

  const send = useCallback(
    (state: S | null) => {
      if (!socket || !topic) return;
      lastSentAt.current = Date.now();
      socket.send({ type: "presence", topic, state });
    },
    [socket, topic],
  );

  useTopic(topic, (frame) => {
    switch (frame.type) {
      case "presence.snapshot":
        setPeers(byUserId(frame.data.peers as PresencePeer<S>[]));
        // A (re)subscription: let the others know this tab is here, even idle.
        send(document.hidden ? null : stateRef.current);
        break;
      case "presence.update": {
        const peer = frame.data as PresencePeer<S>;
        setPeers((prev) => byUserId([...prev.filter((p) => p.user_id !== peer.user_id), peer]));
        break;
      }
      case "presence.leave":
        setPeers((prev) => prev.filter((p) => p.user_id !== frame.data.user_id));
        break;
      case "topic.revoked":
        setPeers([]);
        break;
      default:
        break;
    }
  });

  const report = useCallback(
    (state: S) => {
      stateRef.current = state;
      if (!topic || moveTimer.current !== null) return;
      const wait = Math.max(0, PRESENCE_MOVE_THROTTLE_MS - (Date.now() - lastSentAt.current));
      moveTimer.current = window.setTimeout(() => {
        moveTimer.current = null;
        if (!document.hidden) send(stateRef.current);
      }, wait);
    },
    [topic, send],
  );

  useEffect(() => {
    if (!topic) return;
    const interval = window.setInterval(() => {
      if (!document.hidden) send(stateRef.current);
    }, PRESENCE_TICK_MS);
    const onVisibility = () => send(document.hidden ? null : stateRef.current);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibility);
      if (moveTimer.current !== null) {
        window.clearTimeout(moveTimer.current);
        moveTimer.current = null;
      }
      setPeers([]);
    };
  }, [topic, send]);

  return { peers, report };
}
